/**
 * Analysis Job Status Page
 *
 * Displays real-time status of a running analysis job.
 * Shows workflow step trails for both in-progress and completed analyses.
 * Auto-redirects to results page when job completes while user is watching.
 * Uses WebSocket events for real-time updates instead of polling.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/common';
import { ErrorState } from '../components/ErrorState';
import { AnalysisProgress } from '../components/analysis/AnalysisProgress';
import { PageLayout } from '../components/analysis/AnalysisJobPageLayout';
import { BackButton } from '../components/analysis/AnalysisJobBackButton';
import { useAnalysisJob } from '../hooks/useAnalysisJob';
import { analytics } from '../services/analytics/posthog';
import type { Repository } from '../types';

const AnalysisJobPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const repository = location.state?.repository as Repository | undefined;

  const {
    jobStatus,
    loading,
    error,
    cancelling,
    showDisconnectedBanner,
    elapsedSeconds,
    progressPercent,
    handleCancel,
    handleRetry,
  } = useAnalysisJob(jobId);

  const hasRedirectedRef = useRef(false);
  const initialStatusRef = useRef<string | null>(null);
  const hasSetInitialStatus = useRef(false);

  useEffect(() => {
    analytics.pageViewed('analysis_job');
  }, []);

  useEffect(() => {
    if (!loading && jobStatus?.status && !hasSetInitialStatus.current) {
      initialStatusRef.current = jobStatus.status;
      hasSetInitialStatus.current = true;
    }
  }, [loading, jobStatus?.status]);

  useEffect(() => {
    if (
      !loading &&
      jobStatus?.status === 'completed' &&
      initialStatusRef.current !== 'completed' &&
      !hasRedirectedRef.current
    ) {
      hasRedirectedRef.current = true;
      navigate(`/jobs/${jobId}/results`, { replace: true, state: location.state });
    }
  }, [loading, jobStatus?.status, jobId, navigate, location.state]);

  const handleBackToRepository = () => {
    const repoId = jobStatus?.repository?.id || repository?.id;
    navigate(repoId ? `/repository/${repoId}` : '/repositories');
  };

  const handleNavigateToRepositories = () => navigate('/repositories');
  const handleNavigateToOAuth = () => navigate('/settings?tab=repository');
  const handleNavigateToBilling = () => navigate('/settings/billing');

  const onRetry = async () => {
    const newJobId = await handleRetry();
    if (newJobId) {
      navigate(`/jobs/${newJobId}`, { replace: true });
    }
  };

  if (loading) {
    return (
      <PageLayout pageTitle="Repository Findings" showFooter={false}>
        <div className="flex items-center justify-center pt-32">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Loading analysis...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !jobStatus) {
    return (
      <PageLayout pageTitle="Analysis">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
          <ErrorState
            title="Unable to Load Analysis"
            message={
              error ||
              "We couldn't find this analysis. It may have been removed or the link is incorrect."
            }
            onRetry={onRetry}
            retryText="Start New Analysis"
            type="generic"
          />
        </div>
      </PageLayout>
    );
  }

  if (jobStatus.status === 'completed' && jobStatus.result) {
    return (
      <PageLayout
        pageTitle={jobStatus.repository?.fullName || repository?.fullName || 'Analysis Complete'}
      >
        <BackButton onClick={handleBackToRepository} />
        <AnalysisProgress
          jobStatus={jobStatus}
          progressPercent={100}
          elapsedSeconds={elapsedSeconds}
          showDisconnectedBanner={false}
          cancelling={false}
          onCancel={() => {}}
          onRetry={onRetry}
          onNavigateToRepositories={handleNavigateToRepositories}
          onNavigateToOAuth={handleNavigateToOAuth}
          onNavigateToBilling={handleNavigateToBilling}
          onViewResults={() => navigate(`/jobs/${jobId}/results`, { state: location.state })}
        />
      </PageLayout>
    );
  }

  if (jobStatus.status === 'cancelled') {
    return (
      <PageLayout pageTitle={jobStatus.repository?.fullName || repository?.fullName || 'Analysis'}>
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xs rounded-lg p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 bg-linear-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
              <svg
                className="w-10 h-10 text-gray-600 dark:text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
              Analysis Cancelled
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              The analysis was cancelled before it could complete.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={onRetry}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium shadow-xs"
              >
                Start New Analysis
              </button>
              <button
                onClick={handleNavigateToRepositories}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium shadow-xs"
              >
                Back to Repositories
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={jobStatus.repository?.fullName || repository?.fullName || 'Analysis'}
      showFooter={false}
    >
      <BackButton onClick={handleBackToRepository} />
      <AnalysisProgress
        jobStatus={jobStatus}
        progressPercent={progressPercent}
        elapsedSeconds={elapsedSeconds}
        showDisconnectedBanner={showDisconnectedBanner}
        cancelling={cancelling}
        onCancel={handleCancel}
        onRetry={onRetry}
        onNavigateToRepositories={handleNavigateToRepositories}
        onNavigateToOAuth={handleNavigateToOAuth}
        onNavigateToBilling={handleNavigateToBilling}
      />
    </PageLayout>
  );
};

export default AnalysisJobPage;
export { AnalysisJobPage };
